import Splide from '@splidejs/splide';

const initKeyFiguresGallery = (slider) => {
  const slides = slider.querySelectorAll('.splide__slide').length;

  if (!slides) {
    return;
  }

  const splide = new Splide(slider, {
    type: slides > 1 ? 'loop' : 'slide',
    autoWidth: true,
    gap: 'clamp(0.75rem, 1.53vw, 1.375rem)',
    arrows: slides > 1,
    pagination: false,
    speed: 650,
    drag: slides > 1,
    keyboard: 'focused',
    trimSpace: true,
    breakpoints: {
      1023: {
        gap: '1rem',
      },
      639: {
        gap: '0.75rem',
      },
    },
    reducedMotion: {
      speed: 0,
      rewindSpeed: 0,
    },
  });

  const getElements = () => ({
    track: slider.querySelector('.splide__track'),
    slides: Array.from(
      slider.querySelectorAll('.splide__slide:not(.is-clone)')
    ),
  });

  const getGap = (gallerySlides) =>
    gallerySlides[0]
      ? parseFloat(window.getComputedStyle(gallerySlides[0]).marginRight) || 0
      : 0;

  const hasOverflow = (track, gallerySlides) => {
    if (!track || gallerySlides.length < 2) {
      return false;
    }

    const firstSlide = gallerySlides[0];
    const lastSlide = gallerySlides[gallerySlides.length - 1];
    const contentWidth =
      lastSlide.offsetLeft + lastSlide.offsetWidth - firstSlide.offsetLeft;

    return contentWidth > track.clientWidth + 1;
  };

  const getNextIndex = (currentIndex, track, gallerySlides) => {
    const gap = getGap(gallerySlides);
    let occupiedWidth = 0;
    let index = currentIndex;

    while (index < gallerySlides.length) {
      const slideWidth = gallerySlides[index].offsetWidth;
      const candidateWidth =
        occupiedWidth + (occupiedWidth ? gap : 0) + slideWidth;

      if (candidateWidth > track.clientWidth + 1) {
        break;
      }

      occupiedWidth = candidateWidth;
      index += 1;
    }

    return Math.min(
      Math.max(index, currentIndex + 1),
      gallerySlides.length - 1
    );
  };

  const getPreviousIndex = (currentIndex, track, gallerySlides) => {
    const gap = getGap(gallerySlides);
    let occupiedWidth = 0;
    let targetIndex = Math.max(0, currentIndex - 1);

    for (let index = currentIndex - 1; index >= 0; index -= 1) {
      const slideWidth = gallerySlides[index].offsetWidth;
      const candidateWidth =
        occupiedWidth + (occupiedWidth ? gap : 0) + slideWidth;

      if (candidateWidth > track.clientWidth + 1) {
        break;
      }

      occupiedWidth = candidateWidth;
      targetIndex = index;
    }

    return targetIndex;
  };

  const updateNavigation = () => {
    const { track, slides: gallerySlides } = getElements();
    const isOverflowing = hasOverflow(track, gallerySlides);
    const previousArrow = slider.querySelector('.splide__arrow--prev');
    const nextArrow = slider.querySelector('.splide__arrow--next');

    slider.classList.toggle('is-gallery-overflow', isOverflowing);
    splide.Components.Drag.disable(!isOverflowing);
    splide.Components.Keyboard.disable(!isOverflowing);

    if (!isOverflowing && splide.index !== 0) {
      splide.go(0);
    }

    if (previousArrow) {
      previousArrow.disabled = !isOverflowing;
    }

    if (nextArrow) {
      nextArrow.disabled = !isOverflowing;
    }
  };

  slider.addEventListener(
    'click',
    (event) => {
      const arrow = event.target.closest('.splide__arrow');

      if (!arrow || !slider.classList.contains('is-gallery-overflow')) {
        return;
      }

      const { track, slides: gallerySlides } = getElements();
      const isNext = arrow.classList.contains('splide__arrow--next');
      const targetIndex = isNext
        ? getNextIndex(splide.index, track, gallerySlides)
        : getPreviousIndex(splide.index, track, gallerySlides);

      event.preventDefault();
      event.stopPropagation();
      splide.go(
        targetIndex === splide.index ? (isNext ? '>' : '<') : targetIndex
      );
    },
    true
  );

  splide.on('mounted moved resized', updateNavigation);
  splide.mount();
};

const initAll = () => {
  document.querySelectorAll('[data-key-figures-gallery]').forEach((slider) => {
    if (slider.classList.contains('is-initialized')) {
      return;
    }

    initKeyFiguresGallery(slider);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
